/**
 * MarkdownTile — renders the `body` field of the tile's data as
 * GitHub-flavoured Markdown via `react-markdown` + `remark-gfm`.
 *
 * Safety: react-markdown does NOT render embedded raw HTML (we don't
 * add rehype-raw), and it URL-sanitizes link/image hrefs against a
 * safe-protocol allowlist by default — so agent-supplied markdown
 * can't inject script or `javascript:` links. Components below map
 * each element onto design tokens (square corners, token colours).
 *
 * Expected data shape:
 *   { title?: string; body?: string; meta?: string }
 */
import * as React from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { MarkdownImage } from "@/components/chat/Markdown";
import type { TileBodyProps, TileEditorProps } from "../registry";

interface MarkdownData {
  title?: string;
  body?: string;
  meta?: string;
}

const MD_COMPONENTS: Components = {
  img: MarkdownImage,
  h1: ({ children }) => (
    <h2 className="text-[15px] font-semibold tracking-tight text-fg">{children}</h2>
  ),
  h2: ({ children }) => (
    <h3 className="text-[14px] font-semibold tracking-tight text-fg">{children}</h3>
  ),
  h3: ({ children }) => (
    <h4 className="text-[12.5px] font-semibold tracking-tight text-fg">{children}</h4>
  ),
  p: ({ children }) => <p className="text-fg-muted">{children}</p>,
  ul: ({ children }) => (
    <ul className="ml-4 list-disc space-y-1 text-fg-muted">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="ml-4 list-decimal space-y-1 text-fg-muted">{children}</ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-fg">{children}</strong>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-accent underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  ),
  code: ({ children, className }) => {
    // Block code carries a language- className; inline code doesn't.
    const isBlock = typeof className === "string" && className.includes("language-");
    if (isBlock) {
      return <code className={className}>{children}</code>;
    }
    return (
      <code className="border border-border bg-bg-sunken px-1.5 py-px font-mono text-[12px]">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="overflow-auto border border-border bg-bg-sunken p-2 font-mono text-[11.5px] leading-relaxed text-fg">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-border pl-3 text-fg-muted">{children}</blockquote>
  ),
};

export function MarkdownTile({ data }: TileBodyProps) {
  const d = data as MarkdownData;
  const body = d.body ?? "";
  return (
    <div className="flex h-full flex-col gap-2 text-[13px] leading-relaxed">
      {d.meta && (
        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-faint">
          {d.meta}
        </div>
      )}
      {d.title && (
        <h3 className="text-[14px] font-semibold tracking-tight text-fg">
          {d.title}
        </h3>
      )}
      <div className="space-y-2 text-fg [&_table]:w-full [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1">
        <Markdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
          {body}
        </Markdown>
      </div>
    </div>
  );
}

// ─── Editor ─────────────────────────────────────────────────────────────

/**
 * Inline editor for a markdown tile. Edits the chrome title plus the
 * markdown `body` and optional `meta` line.
 */
export function MarkdownEditor({ data, title, onSave, onCancel }: TileEditorProps) {
  const d = data as MarkdownData;
  const [t, setT] = React.useState(title || d.title || "");
  const [body, setBody] = React.useState(d.body ?? "");
  const [meta, setMeta] = React.useState(d.meta ?? "");

  // Only the kind's own fields: the server refuses unknown ones. The old
  // in-data heading moves to the tile title, which is the one shown now.
  const save = () =>
    onSave({
      title: t.trim(),
      data: {
        body,
        ...(meta.trim() ? { meta: meta.trim() } : {}),
        ...(typeof data.refresh === "string" ? { refresh: data.refresh } : {}),
      },
    });

  return (
    <form
      className="flex h-full flex-col gap-2 text-[13px]"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <input
        value={t}
        onChange={(e) => setT(e.target.value)}
        placeholder="Title"
        className="border border-border bg-bg px-2 py-1 text-[13px] text-fg outline-none focus:border-accent"
      />
      <input
        value={meta}
        onChange={(e) => setMeta(e.target.value)}
        placeholder="Meta (small label, optional)"
        className="border border-border bg-bg px-2 py-1 font-mono text-[11px] text-fg outline-none focus:border-accent"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Markdown body — ## headers, - bullets, `code`"
        className="min-h-0 flex-1 resize-none border border-border bg-bg px-2 py-1 font-mono text-[12px] leading-relaxed text-fg outline-none focus:border-accent"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-7 px-3 text-[12px] text-fg-muted transition hover:text-fg"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="h-7 bg-fg px-3 text-[12px] font-medium text-bg transition hover:bg-accent hover:text-accent-fg"
        >
          Save
        </button>
      </div>
    </form>
  );
}
